import React, { useState } from 'react';
import { filmData } from '../data/mock';
import { 
  Play, 
  Download, 
  Mail, 
  Clock, 
  MapPin, 
  Calendar,
  ChevronDown,
  Award,
  Film,
  Users,
  Quote,
  ExternalLink,
  Menu,
  X,
  Instagram,
  Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: '#about', label: 'About' },
    { href: '#trailer', label: 'Trailer' },
    { href: '#director', label: 'Director' },
    { href: '#gallery', label: 'Gallery' },
    { href: '#cast', label: 'Cast & Crew' },
    { href: '#laurels', label: 'Laurels' },
    { href: '#contact', label: 'Contact' },
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-cream/90 backdrop-blur-md border-b border-maroon/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="#hero" className="font-display text-2xl text-maroon font-bold tracking-wide">
            Ìfé
          </a>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} className="text-maroon/80 hover:text-maroon transition-colors text-sm font-medium">
                {link.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <a 
              href={filmData.pressKitUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-maroon text-cream px-5 py-2.5 rounded-full text-sm font-medium hover:bg-maroon-dark transition-colors"
            >
              Press Kit
            </a>
            <button 
              className="md:hidden text-maroon p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-cream border-t border-maroon/10 py-4 px-6">
            {navLinks.map((link) => (
              <a 
                key={link.href} 
                href={link.href} 
                className="block py-3 text-maroon/80 hover:text-maroon transition-colors font-medium border-b border-maroon/5 last:border-0"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-mustard/20 via-cream to-cream z-0"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-center">
          {/* Poster */}
          <div className="relative group">
            <div className="absolute -inset-4 bg-gradient-to-br from-mustard/40 to-maroon/20 rounded-2xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
            <img 
              src={filmData.posterUrl} 
              alt={`${filmData.title} ${filmData.subtitle} Poster`}
              className="relative rounded-xl shadow-2xl w-full max-w-lg mx-auto transform group-hover:scale-[1.02] transition-transform duration-500"
            />
          </div>
          
          {/* Info */}
          <div className="text-center lg:text-left">
            <p className="text-maroon/70 font-medium mb-2 tracking-widest uppercase text-sm">{filmData.tagline}</p>
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl text-maroon font-bold mb-2">
              {filmData.title}
            </h1>
            <p className="font-display text-3xl md:text-4xl text-mustard-dark mb-6 italic">{filmData.subtitle}</p>
            
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-8">
              <span className="bg-maroon/10 text-maroon px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4" /> {filmData.year}
              </span>
              <span className="bg-maroon/10 text-maroon px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" /> {filmData.runtime}
              </span>
              <span className="bg-maroon/10 text-maroon px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4" /> {filmData.country}
              </span>
              <span className="bg-mustard text-maroon px-4 py-2 rounded-full text-sm font-medium">
                {filmData.genre}
              </span>
            </div>
            
            <p className="text-maroon/80 text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              {filmData.logline}
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
              <a 
                href={filmData.vimeoUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  className="bg-maroon hover:bg-maroon-dark text-cream px-8 py-6 rounded-full text-base font-medium transition-all hover:shadow-xl hover:shadow-maroon/20"
                >
                  <Play className="w-5 h-5 mr-2" /> Watch Trailer
                </Button>
              </a>
              <a 
                href={filmData.pressKitUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  variant="outline"
                  className="border-2 border-maroon text-maroon hover:bg-maroon hover:text-cream px-8 py-6 rounded-full text-base font-medium transition-all"
                >
                  <Download className="w-5 h-5 mr-2" /> Download Press Kit
                </Button>
              </a>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-maroon/40" />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-b from-cream to-mustard/10">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-4xl md:text-5xl text-maroon font-bold text-center mb-4">About the Film</h2>
          <div className="w-24 h-1 bg-mustard mx-auto mb-12 rounded-full"></div>
          
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-xl border border-maroon/5">
            <p className="text-maroon/80 text-lg md:text-xl leading-relaxed mb-8">
              {filmData.introduction}
            </p>
            
            <h3 className="font-display text-2xl text-maroon font-semibold mb-4">Synopsis</h3>
            <p className="text-maroon/70 text-base md:text-lg leading-relaxed">
              {filmData.synopsis}
            </p>
          </div>
        </div>
      </section>

      {/* Trailer Section */}
      <section id="trailer" className="py-24 bg-maroon">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-4xl md:text-5xl text-cream font-bold text-center mb-4">Official Trailer</h2>
          <div className="w-24 h-1 bg-mustard mx-auto mb-12 rounded-full"></div>
          
          <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl">
            <iframe
              src={`https://www.youtube.com/embed/${filmData.youtubeVideoId}?rel=0&modestbranding=1`}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Ìfé - The Sequel Official Trailer"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Director's Statement */}
      <section id="director" className="py-24 bg-maroon">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-4xl md:text-5xl text-cream font-bold text-center mb-4">Director's Statement</h2>
          <div className="w-24 h-1 bg-mustard mx-auto mb-12 rounded-full"></div>
          
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-1">
              <div className="bg-mustard/20 rounded-2xl p-6 text-center">
                <img 
                  src={filmData.director.imageUrl} 
                  alt={filmData.director.name}
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-mustard shadow-lg"
                />
                <h3 className="text-cream font-display text-2xl font-semibold mb-1">{filmData.director.name}</h3>
                <p className="text-cream/70 text-sm mb-4">{filmData.director.role}</p>
                <p className="text-cream/60 text-sm leading-relaxed">
                  {filmData.director.bio.substring(0, 200)}...
                </p>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <Quote className="w-12 h-12 text-mustard/40 mb-4" />
              <div className="text-cream/90 text-base md:text-lg leading-relaxed whitespace-pre-line">
                {filmData.directorsStatement}
              </div>
              <p className="text-mustard font-display text-xl mt-6 italic">— {filmData.director.name}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-4xl md:text-5xl text-maroon font-bold text-center mb-4">Gallery</h2>
          <div className="w-24 h-1 bg-mustard mx-auto mb-12 rounded-full"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filmData.gallery.map((image, idx) => (
              <div 
                key={idx} 
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <img 
                  src={image.url} 
                  alt={image.caption}
                  className="w-full h-64 md:h-80 object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-maroon/80 via-maroon/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <p className="text-cream font-medium p-6">{image.caption}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Vimeo Link */}
          <div className="mt-12 text-center">
            <a 
              href={filmData.vimeoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-maroon hover:bg-maroon-dark text-cream px-8 py-4 rounded-full text-lg font-medium transition-all hover:shadow-xl hover:shadow-maroon/20"
            >
              <Play className="w-5 h-5" /> Watch Trailer on YouTube
            </a>
          </div>
        </div>
      </section>

      {/* Cast & Crew */}
      <section id="cast" className="py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-4xl md:text-5xl text-maroon font-bold text-center mb-4">Cast & Crew</h2>
          <div className="w-24 h-1 bg-mustard mx-auto mb-12 rounded-full"></div>
          
          <Tabs defaultValue="cast" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 bg-maroon/10 p-1 rounded-full">
              <TabsTrigger 
                value="cast" 
                className="rounded-full data-[state=active]:bg-maroon data-[state=active]:text-cream py-3 transition-all flex items-center justify-center"
              >
                <Users className="w-4 h-4 mr-2" /> Cast
              </TabsTrigger>
              <TabsTrigger 
                value="crew"
                className="rounded-full data-[state=active]:bg-maroon data-[state=active]:text-cream py-3 transition-all flex items-center justify-center"
              >
                <Film className="w-4 h-4 mr-2" /> Crew
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="cast">
              {/* Featured Cast */}
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {filmData.cast.filter(c => c.featured).map((member, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg border border-maroon/5 hover:shadow-xl transition-shadow group">
                    <div className="w-20 h-20 bg-gradient-to-br from-mustard to-mustard-dark rounded-full mx-auto mb-4 flex items-center justify-center text-maroon text-2xl font-display font-bold group-hover:scale-110 transition-transform">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <h3 className="text-maroon font-display text-xl font-semibold text-center mb-1">{member.name}</h3>
                    <p className="text-mustard-dark text-center font-medium mb-3">as {member.role}</p>
                    {member.bio && (
                      <p className="text-maroon/60 text-sm text-center leading-relaxed">{member.bio}</p>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Other Cast */}
              <h3 className="text-maroon font-display text-2xl font-semibold mb-6">Supporting Cast</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {filmData.cast.filter(c => !c.featured).map((member, idx) => (
                  <div key={idx} className="bg-maroon/5 rounded-xl p-4 hover:bg-maroon/10 transition-colors">
                    <p className="text-maroon font-medium">{member.name}</p>
                    <p className="text-maroon/60 text-sm">as {member.role}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="crew">
              {/* Key Crew */}
              <div className="grid md:grid-cols-2 gap-6 mb-12">
                {/* Director */}
                <div className="bg-gradient-to-br from-maroon to-maroon-dark rounded-2xl p-6 text-cream">
                  <p className="text-mustard text-sm font-medium mb-1">Director & Producer</p>
                  <h3 className="font-display text-2xl font-semibold mb-2">{filmData.director.name}</h3>
                  <p className="text-cream/70 text-sm leading-relaxed">{filmData.director.bio.substring(0, 300)}...</p>
                </div>
                
                {/* Screenwriter */}
                <div className="bg-gradient-to-br from-mustard to-mustard-dark rounded-2xl p-6 text-maroon">
                  <p className="text-maroon/70 text-sm font-medium mb-1">Screenwriter</p>
                  <h3 className="font-display text-2xl font-semibold mb-2">{filmData.screenwriter.name}</h3>
                  <p className="text-maroon/80 text-sm leading-relaxed">{filmData.screenwriter.bio.substring(0, 300)}...</p>
                </div>
              </div>
              
              {/* Other Crew */}
              <h3 className="text-maroon font-display text-2xl font-semibold mb-6">Production Team</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {filmData.crew.filter(c => c.name !== filmData.director.name && c.name !== filmData.screenwriter.name).map((member, idx) => (
                  <div key={idx} className="bg-maroon/5 rounded-xl p-4 hover:bg-maroon/10 transition-colors">
                    <p className="text-maroon font-medium text-sm">{member.name}</p>
                    <p className="text-maroon/60 text-xs">{member.role}</p>
                  </div>
                ))}
              </div>
              
              {/* Executive Producers */}
              <h3 className="text-maroon font-display text-2xl font-semibold mt-12 mb-6">Executive Producers</h3>
              <div className="flex flex-wrap gap-3">
                {filmData.executiveProducers.map((ep, idx) => (
                  <span key={idx} className="bg-mustard/30 text-maroon px-4 py-2 rounded-full text-sm font-medium">
                    {ep}
                  </span>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Laurels Section */}
      <section id="laurels" className="py-24 bg-gradient-to-b from-mustard/20 to-cream">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-4xl md:text-5xl text-maroon font-bold text-center mb-4">Festival Laurels</h2>
          <div className="w-24 h-1 bg-mustard mx-auto mb-12 rounded-full"></div>
          
          {filmData.laurels.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-12">
              {filmData.laurels.map((laurel, idx) => (
                <div key={idx} className="text-center">
                  <img 
                    src={laurel.imageUrl} 
                    alt={`${laurel.festival} - ${laurel.award}`}
                    className="h-40 md:h-52 w-auto mx-auto mb-4 object-contain"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-12 text-center border border-maroon/10">
              <Award className="w-20 h-20 text-maroon/20 mx-auto mb-4" />
              <h3 className="text-maroon font-display text-2xl font-semibold mb-2">Coming Soon</h3>
              <p className="text-maroon/60 max-w-md mx-auto">
                Ìfé: The Sequel is embarking on its festival journey. Check back for updates on screenings and awards.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Screenings Section */}
      <section className="py-24 bg-cream">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-4xl md:text-5xl text-maroon font-bold text-center mb-4">Screenings</h2>
          <div className="w-24 h-1 bg-mustard mx-auto mb-12 rounded-full"></div>
          
          {filmData.screenings.length > 0 ? (
            <>
              <div className="space-y-4 mb-8">
                {filmData.screenings.map((screening, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-6 shadow-md border border-maroon/5 flex flex-wrap items-center justify-between gap-4 hover:shadow-lg transition-shadow">
                    <div>
                      <h3 className="text-maroon font-semibold text-lg">{screening.festival}</h3>
                      {screening.strand && (
                        <p className="text-mustard-dark font-medium text-sm">{screening.strand} Strand</p>
                      )}
                      <p className="text-maroon/60">{screening.location}</p>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-maroon font-display text-xl font-semibold">{screening.date}</p>
                        <p className="text-mustard-dark font-medium">{screening.time}</p>
                      </div>
                      <a
                        href={screening.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`screening-ticket-btn-${idx}`}
                        className="inline-flex items-center gap-2 bg-maroon hover:bg-maroon-dark text-cream px-5 py-2.5 rounded-full text-sm font-medium transition-all hover:shadow-lg hover:shadow-maroon/20 whitespace-nowrap"
                      >
                        <ExternalLink className="w-4 h-4" /> Get Tickets
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="text-center">
                <p className="text-maroon/60 mt-4 text-sm">More screenings will be announced soon.</p>
              </div>
            </>
          ) : (
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-12 text-center border border-maroon/10">
              <Calendar className="w-20 h-20 text-maroon/20 mx-auto mb-4" />
              <h3 className="text-maroon font-display text-2xl font-semibold mb-2">Screenings Coming Soon</h3>
              <p className="text-maroon/60 max-w-md mx-auto">
                Festival programmers and distributors interested in screening Ìfé: The Sequel, please contact us below.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Press Quotes */}
      {filmData.pressQuotes.length > 0 && (
        <section className="py-24 bg-maroon/5">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="font-display text-4xl md:text-5xl text-maroon font-bold text-center mb-4">Press</h2>
            <div className="w-24 h-1 bg-mustard mx-auto mb-12 rounded-full"></div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {filmData.pressQuotes.map((quote, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg">
                  <Quote className="w-10 h-10 text-mustard mb-4" />
                  <p className="text-maroon text-lg italic mb-4">"{quote.text}"</p>
                  <p className="text-maroon/60">— {quote.source}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-cream to-mustard/20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-4xl md:text-5xl text-maroon font-bold text-center mb-4">Contact</h2>
          <div className="w-24 h-1 bg-mustard mx-auto mb-12 rounded-full"></div>
          
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-maroon/5 text-center">
            <p className="text-maroon/70 text-lg mb-8">
              For festival submissions, press inquiries, distribution opportunities, and general information about Ìfé: The Sequel.
            </p>
            
            <a 
              href={`mailto:${filmData.contactEmail}`}
              className="inline-flex items-center gap-3 bg-maroon hover:bg-maroon-dark text-cream px-8 py-4 rounded-full text-lg font-medium transition-all hover:shadow-xl hover:shadow-maroon/20"
            >
              <Mail className="w-5 h-5" />
              {filmData.contactEmail}
            </a>
            
            <a 
              href={filmData.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-mustard hover:bg-mustard-dark text-maroon px-8 py-4 rounded-full text-lg font-medium transition-all hover:shadow-xl hover:shadow-mustard/20 ml-4"
            >
              <Instagram className="w-5 h-5" />
              @{filmData.instagram}
            </a>
            
            <div className="mt-12 pt-8 border-t border-maroon/10">
              <p className="text-maroon/60 text-sm mb-4">Production Company</p>
              <p className="text-maroon font-display text-2xl font-semibold">{filmData.productionCompany.name}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-maroon py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-display text-3xl text-cream font-bold">Ìfé <span className="text-mustard">The Sequel</span></h3>
              <p className="text-cream/60 mt-1">A Pamela Adie Film • {filmData.year}</p>
            </div>
            
            <div className="flex items-center gap-6">
              <a 
                href={filmData.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cream/70 hover:text-mustard transition-colors flex items-center gap-2"
              >
                <Instagram className="w-4 h-4" /> @{filmData.instagram}
              </a>
              <a 
                href={filmData.pressKitUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cream/70 hover:text-mustard transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Press Kit
              </a>
              <a 
                href={`mailto:${filmData.contactEmail}`}
                className="text-cream/70 hover:text-mustard transition-colors flex items-center gap-2"
              >
                <Mail className="w-4 h-4" /> Contact
              </a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-cream/10 text-center">
            <p className="text-cream/40 text-sm">
              © {new Date().getFullYear()} {filmData.productionCompany.name}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default HomePage;
